import React, { useEffect, useState, useContext } from "react";
import { UserContext } from "../context/UserContext";
import { PieChart, Pie } from "recharts";
import api from "../Api";

const COLORS = {
  closed: "#285330",  
  open: "#42844f",    
  pending: "#5fd175"  
};

export default function ActivityOverview({ selectedStartDate, selectedEndDate }) {
  const [data, setData] = useState({ closed: 0, open: 0, pending: 0 });
  const { user } = useContext(UserContext);

  useEffect(() => {
    const fetchStats = async () => {
      // Usa le date passate come props, oppure fallback al mese corrente
      let start, end;
      if (selectedStartDate && selectedEndDate) {
        start = selectedStartDate.toISOString().split("T")[0];
        end = selectedEndDate.toISOString().split("T")[0];
      } else {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        start = new Date(year, month, 1).toISOString().split("T")[0];
        end = new Date(year, month + 1, 0).toISOString().split("T")[0];
      }

      try {
        let endpoint = "/activity/range";
        let params = { start, end };
        if (user && user.role === "rup") {
          endpoint = "/activity/worksite-range";
          params.worksite = user.worksite;
        }
        const res = await api.get(endpoint, { params });
        setData(res.data);
      } catch (error) {
        console.error("Errore fetch stats:", error);
      }
    };

    fetchStats();
  }, [user, selectedStartDate, selectedEndDate]);

  const total = data.closed + data.open + data.pending;
  const closedPercent = total > 0 ? Math.round((data.closed / total) * 100) : 0;

  const getAngle = (value) => {
    if (total === 0) return 0;
    return (value / total) * 360;
  };

  const getCurrentMonth = () => {
    const raw = new Date().toLocaleDateString("it-IT", {
      month: "long",
      year: "numeric"
    });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const ringThickness = 15;
  const baseRadius = 110;

  const layers = [
    {
      name: "pending",
      value: data.pending,
      color: COLORS.pending,
      innerRadius: baseRadius - ringThickness * 2,
      outerRadius: baseRadius - ringThickness
    },
    {
      name: "open",
      value: data.open,
      color: COLORS.open,
      innerRadius: baseRadius - ringThickness,
      outerRadius: baseRadius
    },
    {
      name: "closed",
      value: data.closed,
      color: COLORS.closed,
      innerRadius: baseRadius,
      outerRadius: baseRadius + ringThickness
    }
  ];

  return (
    <div className="col-span-2 bg-primary rounded-2xl shadow p-6 h-full border-2 border-secondary flex flex-col gap-6 text-white">

      {/* Titolo + mese */}
      <div>
        <h2 className="text-lg font-semibold">Panoramica delle attività</h2>
        <p className="text-sm  text-smokey">{getCurrentMonth()}</p>
      </div>

      {/* Grafico + legenda */}
      <div className="flex flex-col md:flex-row items-center justify-around gap-8 flex-1">
        {/* Donut Chart */}
        <div className="relative w-[320px] h-[320px]">
          <PieChart width={320} height={320}>
            {layers.map((layer, index) => (
              <Pie
                key={index}
                data={[{ value: layer.value }]}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={layer.innerRadius}
                outerRadius={layer.outerRadius}
                fill={layer.color}
                startAngle={0}
                endAngle={90 + getAngle(layer.value)}
                cornerRadius={10}
                stroke="none"
                isAnimationActive={true}
                animationDuration={1000}
                animationEasing="ease-out"
              />
            ))}
          </PieChart>

          {/* Cerchio interno */}
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <div className="w-[160px] h-[160px] bg-global rounded-full flex items-center justify-center">
              <span className="text-white text-2xl font-bold">{closedPercent}%</span>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-5 text-lg">
          <div className="flex items-center gap-3">
            <span className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS.pending }}></span>
            In attesa: {data.pending}
          </div>
          <div className="flex items-center gap-3">
            <span className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS.open }}></span>
            In corso: {data.open}
          </div>
          <div className="flex items-center gap-3">
            <span className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS.closed }}></span>
            Chiuse: {data.closed}
          </div>
        </div>
      </div>
    </div>
  );
}
